import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { BookOpen, Shield, Folder, Globe, Bot, X } from 'lucide-react';
import AppsTable from './AppsTable';
import MultiChipFilters from './MultiChipFilters';
const API = process.env.REACT_APP_API_BASE;


const AppManagement = () => {
  const [apps, setApps] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [filteredApps, setFilteredApps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: '',
    category: '',
    subcategories: [],
    tags: [],
    countries: [],
    icon: '',
    disabled: false
  });
  const [editingApp, setEditingApp] = useState(null);
  const [filters, setFilters] = useState({ name: '', tag: '', country: '', category: '' });

  const [allCountries, setAllCountries] = useState([]);
useEffect(() => {
  fetch(`${API}/api/countries`)
    .then(res => res.json())
    .then(data => setAllCountries(data))
    .catch(() => toast.error('Failed to load countries'));
}, []);

  const iconOptions = [
    { label: 'Knowledge', value: 'BookOpen', icon: <BookOpen size={18} /> },
    { label: 'Security', value: 'Shield', icon: <Shield size={18} /> },
    { label: 'Documents', value: 'Folder', icon: <Folder size={18} /> },
    { label: 'Global', value: 'Globe', icon: <Globe size={18} /> },
    { label: 'AI Bot', value: 'Bot', icon: <Bot size={18} /> }
  ];

  useEffect(() => {
    fetchApps();
    fetchCategories();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [apps, filters]);

  const fetchApps = async () => {
    try {
      const res = await fetch(`${API}/admin/apps`);
      const data = await res.json();
      setApps(data);
    } catch {
      toast.error('Failed to fetch apps');
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/admin/categories`);
      const data = await res.json();
      setCategories(data);
    } catch {
      toast.error('Failed to fetch categories');
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => {
    const filtered = apps.filter(app => {
      const nameMatch = app.name.toLowerCase().includes(filters.name.toLowerCase());
      const tagMatch = !filters.tag || app.tags.includes(filters.tag);
      const countryMatch = !filters.country || app.countries.includes(filters.country);
      const categoryMatch = !filters.category || app.categories?.some(cat => cat._id === filters.category);
      return nameMatch && tagMatch && countryMatch && categoryMatch;
    });
    setFilteredApps(filtered);
  };

  const handleTagInput = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      setForm({ ...form, tags: [...form.tags, e.target.value.trim()] });
      e.target.value = '';
      e.preventDefault();
    }
  };

  const handleCountryInput = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      const val = e.target.value.trim();
      const matched = allCountries.find(c => c.name.toLowerCase() === val.toLowerCase());
if (matched && !form.countries.includes(matched._id)) {
  setForm({ ...form, countries: [...form.countries, matched._id] });
} else {
  toast.error('Invalid country');
}
      e.target.value = '';
      e.preventDefault();
    }
  };

  const handleTagRemove = tag => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  const handleCountryRemove = country => {
    setForm({ ...form, countries: form.countries.filter(c => c !== country) });
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubcategoryToggle = (sub) => {
    const exists = form.subcategories.find(
      s => s.name === (sub.name || sub) && s.categoryId === form.category
    );
    if (exists) {
      setForm({
        ...form,
        subcategories: form.subcategories.filter(
          s => !(s.name === (sub.name || sub) && s.categoryId === form.category)
        )
      });
    } else {
      setForm({
        ...form,
        subcategories: [...form.subcategories, { name: sub.name || sub, categoryId: form.category }]
      });
    }
  };

  const removeSubcategory = (name, categoryId) => {
    setForm({
      ...form,
      subcategories: form.subcategories.filter(
        s => !(s.name === name && s.categoryId === categoryId)
      )
    });
  };

  const handleSubmit = async () => {
    if (!form.name || !form.category) {
      toast.error('App name and category are required');
      return;
    }

    const method = editingApp ? 'PUT' : 'POST';
    const url = editingApp
      ? `${API}/admin/apps/${editingApp._id}`
      : `${API}/admin/apps`;

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success(editingApp ? 'App updated' : 'App created');
        fetchApps();
        setForm({ name: '', category: '', subcategories: [], tags: [], countries: [], icon: '', disabled: false });
        setEditingApp(null);
      } else {
        toast.error('Failed to save app');
      }
    } catch {
      toast.error('Server error');
    }
  };

  const handleEdit = (app) => {
    setEditingApp(app);
    setForm({
      name: app.name,
      category: app.categories[0]?._id || '',
      subcategories: app.subcategories || [],
      tags: app.tags || [],
      countries: app.countries || [],
      icon: app.icon || '',
      disabled: !app.enabled,
      routeKey: app.routeKey,
      status: app.status,
    });
  };

  const selectedCategory = categories.find(cat => cat._id === form.category);
  const availableSubcategories = selectedCategory?.subcategories || [];

  return (
    <div className="space-y-6">
      

      <div className="bg-white border p-4 rounded-md shadow space-y-4">
        <h3 className="text-lg font-semibold">{editingApp ? 'Edit App' : 'Create New App'}</h3>

        <input name="name" value={form.name} onChange={handleChange} placeholder="App name" className="border px-3 py-2 rounded w-full" />
        <input
  type="text"
  name="routeKey"
  value={form.routeKey}
  onChange={handleChange}
  placeholder="App Route e.g., smart-doc, content-studio"
  className="w-full p-2 border rounded mb-4"
  required
/>
        <select name="category" value={form.category} onChange={handleChange} className="border px-3 py-2 rounded w-full">
          <option value="">Select Category</option>
          {categories.map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
        </select>
        {!availableSubcategories.length && form.category && (
  <p className="text-sm text-muted-foreground mt-1 italic">
    No subcategories available for <strong>{selectedCategory?.name}</strong>. You can still assign this category.
  </p>
)}

        {availableSubcategories.length > 0 && (
          <>
            <p className="text-sm font-medium">Subcategories</p>
            <div className="flex flex-wrap gap-2">
              {availableSubcategories.map((sub, idx) => (
                <label key={idx} className="flex items-center gap-1 text-sm border px-2 py-1 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.subcategories.some(s => s.name === (sub.name || sub) && s.categoryId === form.category)}
                    onChange={() => handleSubcategoryToggle(sub)}
                  />
                  {sub.name || sub}
                </label>
              ))}
            </div>
          </>
        )}

        {form.subcategories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {form.subcategories.map((sub, idx) => (
              <span key={idx} className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs flex items-center gap-1">
                {sub.name}
                <button onClick={() => removeSubcategory(sub.name, sub.categoryId)}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <input onKeyDown={handleTagInput} placeholder="Press enter to add tags" className="border px-3 py-2 rounded w-full" />
        <div className="flex flex-wrap gap-2">
          {form.tags.map(tag => (
            <span key={tag} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
              {tag} <button onClick={() => handleTagRemove(tag)}>&times;</button>
            </span>
          ))}
        </div>

        <input onKeyDown={handleCountryInput} placeholder="Press enter to add countries" className="border px-3 py-2 rounded w-full" />
        <div className="flex flex-wrap gap-2">
        {form.countries.map(countryId => {
  const country = allCountries.find(c => c._id === countryId);
  return (
    <span key={countryId} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
      {country?.name || 'Unknown'}
      <button onClick={() => handleCountryRemove(countryId)}>&times;</button>
    </span>
  );
})}
        </div>
        <select name="status" value={form.status} onChange={handleChange} className="border px-3 py-2 rounded w-full">
          <option value="">Select Launch Status</option>
           <option key="New">New</option>
           <option key="New">Hot</option>
           <option key="New">Beta</option>
           <option key="New">Coming Soon</option>
        </select>
        <div className="space-y-1">
          <label className="text-sm font-medium">Select Icon</label>
          <div className="flex gap-4 flex-wrap">
            {iconOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setForm({ ...form, icon: opt.value })}
                className={`flex items-center gap-1 border px-3 py-2 rounded ${form.icon === opt.value ? 'bg-blue-100 border-blue-500' : ''}`}
              >
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.disabled}
            onChange={(e) => setForm({ ...form, disabled: e.target.checked })}
          />
          Disable this app
        </label>

        <div className="flex gap-2">
          <button onClick={handleSubmit} className="bg-blue-600 text-white px-4 py-2 rounded">
            {editingApp ? 'Update App' : 'Save App'}
          </button>
          {editingApp && (
            <button onClick={() => {
              setEditingApp(null);
              setForm({ name: '', category: '', subcategories: [], tags: [], countries: [], icon: '', disabled: false, status: '', routeKey: ''  });
            }} className="text-sm underline">
              Cancel
            </button>
          )}
        </div>
      </div>
      <div className="bg-white border p-4 rounded-md shadow space-y-4">
        <h3 className="text-lg font-semibold">Search Apps</h3>
        <div className="flex flex-wrap gap-2">
        <MultiChipFilters
  allTags={[...new Set(apps.flatMap(app => app.tags || []))]}
  allCountries={[...new Set(apps.flatMap(app => app.countries || []))]}
  allCategories={categories}
  onApplyFilters={(applied) => {
    const filtered = apps.filter(app => {
      const tagMatch = applied.tags.length === 0 || applied.tags.some(tag => app.tags.includes(tag));
      const countryMatch = applied.countries.length === 0 || applied.countries.some(country => app.countries.includes(country));
      const subcatMatch = applied.subcategories.length === 0 || applied.subcategories.some(sub =>
        app.subcategories.some(appSub => appSub.name === sub.name && appSub.categoryId === sub.categoryId)
      );
      return tagMatch && countryMatch && subcatMatch;
    });

    setFilteredApps(filtered);
    setCurrentPage(1);
  }}
/>

        </div>
      </div>
      <AppsTable apps={filteredApps} onEdit={handleEdit} onRefresh={fetchApps} />
    </div>
  );
};

export default AppManagement;
